import React from 'react';
import { Loader2, TrendingUp, Users, FileText, DollarSign } from 'lucide-react';
import { cn } from '../../lib/utils';
import { slidesBotApi } from '../../api/slidesBot';
import { PanelCard, useFetchOnce } from './index';

interface Props {
  isDark: boolean;
}

const fmtUzs = (n: number) =>
  n.toLocaleString('ru-RU').replace(/,/g, ' ') + ' soʼm';

const fmtUsd = (n: number) => '$' + n.toFixed(2);

export default function StatsView({ isDark }: Props) {
  const { data, loading, error } = useFetchOnce(() => slidesBotApi.stats());

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <PanelCard isDark={isDark}>
        <p className="text-red-500">Failed to load: {error}</p>
      </PanelCard>
    );
  }

  const margin =
    data.revenue_total_uzs > 0
      ? Math.round(
          ((data.revenue_total_uzs - data.cost_usd_total * 12500) /
            data.revenue_total_uzs) *
            100,
        )
      : 0;

  return (
    <div className="grid gap-4">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi
          isDark={isDark}
          icon={<DollarSign className="w-5 h-5" />}
          label="Revenue (today)"
          value={fmtUzs(data.revenue_today_uzs)}
          sub={`Week: ${fmtUzs(data.revenue_week_uzs)}`}
        />
        <Kpi
          isDark={isDark}
          icon={<DollarSign className="w-5 h-5" />}
          label="Revenue (total)"
          value={fmtUzs(data.revenue_total_uzs)}
          sub={`Margin ~${margin}%`}
        />
        <Kpi
          isDark={isDark}
          icon={<FileText className="w-5 h-5" />}
          label="Decks today"
          value={String(data.decks_completed_today)}
          sub={`Week: ${data.decks_completed_week} · Total: ${data.decks_completed_total}`}
        />
        <Kpi
          isDark={isDark}
          icon={<TrendingUp className="w-5 h-5" />}
          label="Conversion"
          value={`${data.conversion_pct}%`}
          sub={`${data.paying_users} of ${data.total_users} paid`}
        />
      </div>

      {/* Secondary breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi
          isDark={isDark}
          icon={<Users className="w-5 h-5" />}
          label="Total users"
          value={String(data.total_users)}
          sub={`${data.linked_users} linked to Talabaa.uz`}
        />
        <Kpi
          isDark={isDark}
          icon={<Users className="w-5 h-5" />}
          label="Paying users"
          value={String(data.paying_users)}
          sub="Bought ≥ 1 package"
        />
        <Kpi
          isDark={isDark}
          icon={<DollarSign className="w-5 h-5" />}
          label="AI cost (total)"
          value={fmtUsd(data.cost_usd_total)}
          sub={`Today: ${fmtUsd(data.cost_usd_today)}`}
        />
      </div>
    </div>
  );
}

function Kpi({
  isDark,
  icon,
  label,
  value,
  sub,
}: {
  isDark: boolean;
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <PanelCard isDark={isDark}>
      <div className="flex items-center gap-3 mb-2 text-[#0EA5A4]">
        {icon}
        <span
          className={cn(
            'text-xs uppercase tracking-wide font-medium',
            isDark ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {label}
        </span>
      </div>
      <p
        className={cn(
          'text-2xl font-bold',
          isDark ? 'text-white' : 'text-[#0F172A]',
        )}
      >
        {value}
      </p>
      {sub && (
        <p
          className={cn(
            'text-xs mt-1',
            isDark ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {sub}
        </p>
      )}
    </PanelCard>
  );
}
